import type { CoverAsset } from '../domain/entities/types';
import { createId, nowInstant } from '../utils/core';

const loadImage = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  const url = URL.createObjectURL(file);
  image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
  image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível ler a imagem.')); };
  image.src = url;
});

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error('Não foi possível otimizar a capa.')),
    type,
    quality
  ));

const drawResized = (image: HTMLImageElement, maxDimension: number, failureMessage: string) => {
  const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error(failureMessage);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, width, height);
  return { canvas, height, width };
};

const encodeWebpOrJpeg = async (canvas: HTMLCanvasElement, quality: number): Promise<Blob> => {
  try {
    const webp = await canvasToBlob(canvas, 'image/webp', quality);
    if (webp.type === 'image/webp') return webp;
  } catch {
    // Fall through to the broadly supported JPEG encoder.
  }
  return canvasToBlob(canvas, 'image/jpeg', quality);
};

const COVER_MAX_BYTES = 1_000_000;
const COVER_QUALITIES = [0.82, 0.68, 0.54, 0.42] as const;

export const imageService = {
  async processCover(file: File): Promise<CoverAsset> {
    if (!file.type.startsWith('image/')) throw new Error('Selecione uma imagem válida.');
    const image = await loadImage(file);
    const { canvas, height, width } = drawResized(image, 1200, 'O navegador não conseguiu processar a capa.');
    let blob: Blob | undefined;
    for (const quality of COVER_QUALITIES) {
      blob = await encodeWebpOrJpeg(canvas, quality);
      if (!['image/webp', 'image/jpeg'].includes(blob.type)) {
        throw new Error('O navegador não conseguiu gerar uma capa compatível.');
      }
      if (blob.size <= COVER_MAX_BYTES) break;
    }
    if (!blob || blob.size > COVER_MAX_BYTES) throw new Error('A capa continua muito grande após a otimização.');
    const now = nowInstant();
    return {
      id: createId(),
      data: blob,
      mimeType: blob.type,
      width,
      height,
      byteSize: blob.size,
      createdAt: now,
      updatedAt: now
    };
  },

  async processAvatar(file: File): Promise<Blob> {
    if (!file.type.startsWith('image/')) throw new Error('Selecione uma foto em formato de imagem.');
    if (file.size > 10 * 1024 * 1024) throw new Error('A foto original deve ter no máximo 10 MB.');
    const image = await loadImage(file);
    const { canvas } = drawResized(image, 512, 'O navegador não conseguiu processar a foto.');
    let blob = await encodeWebpOrJpeg(canvas, 0.84);
    if (blob.size > 400_000) blob = await encodeWebpOrJpeg(canvas, 0.7);
    if (blob.size > 400_000) blob = await encodeWebpOrJpeg(canvas, 0.58);
    if (blob.size > 400_000) throw new Error('A foto continua muito grande após a otimização.');
    if (!['image/webp', 'image/jpeg'].includes(blob.type)) {
      throw new Error('O navegador não conseguiu gerar uma foto compatível.');
    }
    return blob;
  }
};
